import { SimpleTableConfig, SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import { TEMPLATE_TAG_SETS } from "@/lib/types/task-templates";
import { TAG_SETS } from "@/components/ui/tags/CommonTagChips";
import { TASK_CORE_FIELDS } from "./config/fields/task-core-fields";
import { TASK_STATUS_SCHEDULE_FIELDS } from "./config/fields/task-status-schedule-fields";
import { taskSavedViews } from "./config/task-saved-views";
import { ENHANCED_TASK_DETAIL_CONFIG } from "./EnhancedTaskDetailConfig";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/** Logical order for create task form: template → what → who → status/dates → assignment → tags */
const CREATE_TASK_FIELD_ORDER: string[] = [
  "template",
  "title",
  "description",
  "contact_id",
  "company_id",
  "status",
  "priority",
  "due_date",
  "assigned_users",
  "tags",
];

/**
 * Progressive-disclosure section layout for the Create Task form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_TASK_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["template", "title", "status", "priority"],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    summary: "Due date and assignees",
    fieldNames: ["due_date", "assigned_users"],
  },
  {
    id: "relationships",
    kind: "collapsible",
    summary: "Link to a contact or company",
    fieldNames: ["contact_id", "company_id"],
  },
  {
    id: "description",
    kind: "collapsible",
    title: "Description & tags",
    fieldNames: ["description", "tags"],
  },
];

/**
 * Unified Task Configuration
 * This single configuration serves both table display and form creation/editing
 * Eliminates duplication between multiple task config files
 */
export const TASK_CONFIG: SimpleTableConfig<ContactTaskRow> = {
  entity: "tasks",
  displayName: "Task",
  statusFieldId: "status",
  defaultPageSize: 10,
  boardViewPageSize: 100,
  defaultSort: { field: "created_at", direction: "desc" },
  autoIncludeUserId: true, // Auto-populate user_id for task creation

  createFormFieldOrder: CREATE_TASK_FIELD_ORDER,
  createFormSections: CREATE_TASK_FORM_SECTIONS,

  /** Default table column order: task → status → priority → when → who → contact */
  tableColumnOrder: [
    "title",
    "status",
    "priority",
    "due_date",
    "assigned_users",
    "contact_id",
  ],

  /** Card view: title → status → priority → due date → who → assignees (scan before opening) */
  cardFaceFieldOrder: [
    "title",
    "status",
    "priority",
    "due_date",
    "contact_id",
    "assigned_users",
  ],

  mobileDetailSections: [
    { id: "setup", label: "Template" },
    { id: "basic", label: "Task" },
    { id: "contact", label: "Contact & Company" },
    { id: "status", label: "Status" },
    { id: "schedule", label: "Schedule & Assignment" },
    { id: "tags", label: "Tags" },
    { id: "metadata", label: "Metadata" },
  ],

  /**
   * List → detail modal only (`SimpleTableConfig.detailModalUi`).
   * See `.cursor/rules/DETAIL_MODAL_UI_AND_AI_SUMMARY.mdc` — AI summary is a compact launcher; full UI expands inline in the same modal section.
   */
  detailModalUi: {
    aiSummaryMinimalProminence: true,
  },

  fields: {
    ...TASK_CORE_FIELDS,
    ...TASK_STATUS_SCHEDULE_FIELDS,
  },

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  // Add detail page actions for table rows
  detailActions: ENHANCED_TASK_DETAIL_CONFIG.actions,

  // Show row actions inline with the title column
  rowActionsPlacement: "inlineTitle",
  rowActionsDisplayMode: "iconOnly",

  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    columnResizing: true,
    columnVisibility: true,
    savedViews: true,
    infiniteScroll: true,
    multiColumnSort: true,
    calendarView: true,
  },

  savedViews: taskSavedViews,

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: {},
};

/**
 * Tag sets for task templates
 */
export const taskTemplateTagSets = {
  TASK: TAG_SETS.TASK,
  SCHOOLEY_MITCHELL: TAG_SETS.SCHOOLEY_MITCHELL,
  WEST_TENNESSEE_CONSULTING: TAG_SETS.WEST_TENNESSEE_CONSULTING,
  ...TEMPLATE_TAG_SETS,
};
